import {
  OwnerId,
  type ReplaceTaskDelegation,
  type TaskDelegation
} from 'prove-common'
import { type DelegationNode } from './taskDelegationUtils'

export interface TaskDelegationDraft {
  plannedQuantity: number
  nodes: DelegationNode[]
}

export const delegationViewRoot = (
  delegations: DelegationNode[],
  workspaceRoot: OwnerId[]
): OwnerId[] => {
  if (workspaceRoot.length === 0 || delegations.length > 1) return []
  if (delegations.length === 1) return [delegations[0].ownerId]
  return workspaceRoot
}

const parseOwner = (value: string): OwnerId => {
  const ownerId = OwnerId.fromString(value)
  if (ownerId == null) throw new Error(`Ugyldig eier i fordelingen: ${value}`)
  return ownerId
}

const pathKey = (path: string[]): string => JSON.stringify(path)

export const delegationToDraft = (
  delegation: TaskDelegation
): TaskDelegationDraft => {
  if (delegation.entries.length === 0) {
    return {
      plannedQuantity: delegation.plannedQuantity,
      nodes: []
    }
  }

  const entriesByParentPath = new Map<string, TaskDelegation['entries']>()
  for (const entry of delegation.entries) {
    const parentKey = pathKey(entry.path.slice(0, -1))
    const entries = entriesByParentPath.get(parentKey) ?? []
    entries.push(entry)
    entriesByParentPath.set(parentKey, entries)
  }

  const buildChildren = (parentPath: string[]): DelegationNode[] =>
    (entriesByParentPath.get(pathKey(parentPath)) ?? []).map((entry) => {
      const ownerId = parseOwner(entry.ownerId)
      const children = buildChildren(entry.path)
      return {
        ownerId,
        plannedQuantity: entry.plannedQuantity,
        retainedQuantity: entry.retainedQuantity,
        samplesTaken: entry.samplesTaken,
        children
      }
    })

  return {
    plannedQuantity: delegation.plannedQuantity,
    nodes: buildChildren([])
  }
}

export const draftToDelegationReplacement = (
  draft: TaskDelegationDraft
): ReplaceTaskDelegation => {
  const entries: ReplaceTaskDelegation['entries'] = []
  const append = (nodes: DelegationNode[], parentPath: string[]): void => {
    for (const node of nodes) {
      const path = [...parentPath, node.ownerId.toString()]
      entries.push({ path, plannedQuantity: node.plannedQuantity })
      append(node.children, path)
    }
  }
  append(draft.nodes, [])
  return {
    entries
  }
}
